import React, { ChangeEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { useNavigate } from 'react-router-dom';
import { ArrowDownTrayIcon, ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon, PencilSquareIcon, PrinterIcon, TrashIcon } from '@heroicons/react/24/solid';
import type { ColumnDef } from '@tanstack/react-table'

import { CheckClass, getStatusColor, IconClass, InputClass, SmallButtonClass } from 'contexts/style';
import options from "contexts/options.json"
import { getAge, JSTDate, JSTTime, today } from 'contexts/dateUtils';
import { Loading } from 'components/Loading';
import { DataTable, TableHeadBase, createColumnsFromHeads, getNestedValue } from 'components/DataTable';
import { useFirebaseApp } from 'contexts/FirebaseApp';
import { filterStates } from 'database/AppApolloProvider';
import { DatePicker, FilterInput } from 'components/UIParts';
import makePdf from './sheet';
import showMessage from 'components/showMessage';
import { INTERNAL_TYPE, OUTER_BOOK_TYPE, OUTSIDE_TYPE } from 'contexts/enviroments';
import { useMongoDelete } from 'contexts/useMongoQuery';
import { cancelBookingMail } from 'contexts/cancelUtils';
//        makePdf(data.attendances, yearMonth, `${user?.surname || ""}　${user?.givenName || ""}`)

// Helper function that provides type context
const createHeads = <T extends readonly TableHeadBase[]>(configs: T): T => configs

const heads = createHeads([
    { field: "bookingStatus", text: "ステータス", type: "Status" },
    { field: "start_date", text: "日付", id: "start", type: "Date" },
    { field: "checkin", text: "受付時間", type: "TimeString" },
    { field: "ivCheckin", text: "IV時間", type: "TimeString" },
    { field: "start_time", text: "予約時間", id: "start", type: "Time" },
    { field: "patientID", text: "患者ID" },
    { field: "patientName", text: "患者名" },
    { field: "patient.nameKana", text: "患者名カナ" },
    { field: "modalityID", text: "モダリティ" },
    { field: "body2", text: "部位" },
    { field: "contrast", text: "造影剤", type: "Contrast" },
    { field: "bookingType", text: "種別", type: "Option" },
    { field: "courseName", text: "コース" },
    { field: "patient.gender", text: "性別", type: "Option", optionKey: "genderType" },
    { field: "patient.birthDate", text: "生年月日", type: "Date" },
    { field: "patient.age", text: "年齢", id: "patient.birthDate", type: "Age" },
    { field: "height", text: "身長" },
    { field: "weight", text: "体重" },
    { field: "_id", text: "アクセッション番号" },
    { field: "begin", text: "開始時間", type: "TimeString" },
    { field: "checkout", text: "終了時間", type: "TimeString" },
])

const statusSet = [
    { key: 0, value: "仮予約" },
    { key: 1, value: "予約確定" },
    { key: 2, value: "IV実施" },
    { key: 3, value: "検査中" },
    { key: 4, value: "実施済" },
    { key: 5, value: "来院取消" },
]


const getQueryVariables = (values: KV) => {
    let output: KV = { start: { $gte: values.start }, end: { $lte: values.end } }
    if (values.patientID) output.patientID = values.patientID
    if (values.patientName) output.patient = { nameKana: { $regex: values.patientName } }
    return output
}

export const List = () => {
    const { resources, functions } = useFirebaseApp()
    const navigate = useNavigate()
    const values: KV = useReactiveVar(filterStates)
    const names = values.bookingList || { modality: "", modality2: "", statuses: [0, 1, 2, 3, 4], start: today(), end: new Date(today().getTime() + 86399999), oneday: true, patientID: "", patientName: "" }
    const namesRef = useRef(names)
    namesRef.current = names

    const [loading, setLoading] = useState(false)
    const [data, setData] = useState<KV[]>([])
    const [deleteDataMutation, { loading: deleting }] = useMongoDelete("bookings")

    const baseResources = useMemo(() => resources.filter(v => !v.exam), [resources])
    const baseResourceIds = useMemo(() => baseResources.map(v => v.id as string), [baseResources])
    const noslotResources = useMemo(() => baseResources.filter(v => v.noslot).map(v => v.id as string), [baseResources])

    const showingData = useMemo(() => {
        const modalityFilter = names.modality || baseResourceIds
        return data.filter(datum =>
            modalityFilter.includes(datum.modalityID) || names.modality2 === datum.modalityID
        ).filter(datum => names.statuses.includes(datum.bookingStatus))
    }, [data, names.modality, names.modality2, names.statuses, baseResourceIds])

    const getBookings = useCallback(async () => {
        setLoading(true)
        const query = getQueryVariables(namesRef.current)
        console.log(query)
        const patientQuery = query.patient ? { ...query.patient } : undefined
        delete query.patient
        let stages: KV[] = [
            { $match: query },
            {
                $lookup: {
                    from: "patients",
                    localField: "patient",
                    foreignField: "patientId",
                    as: "patient"
                }
            },
            { $unwind: { path: "$patient", preserveNullAndEmptyArrays: true } }
        ]
        if (patientQuery) stages.push({ $match: { "patient.nameKana": patientQuery.nameKana } })
        const data = await functions(["mongo/client", { collection: "bookings", aggregate: { pipeline: stages } }]) as KV[];
        setData(data)
        setLoading(false)
    }, [functions])

    const handleDelete = useCallback(async (id: string) => {
        if (!await showMessage("予約を完全に削除します。よろしいですか？", { confirm: true })) return null
        try {
            // 外部予約または外注で、医師が設定されている場合、メールを送信する
            const booking = await functions(["mongo/client", { collection: "bookings", findOne: { filter: { _id: id } } }]) as KV;
            const doctor = await functions(["mongo/client", { collection: "user", findOne: { filter: { _id: booking.doctorID } } }]) as KV;
            const external = await functions(["mongo/client", { collection: "user", find: { filter: { _id: String(booking.doctorID) } } }]) as KV[];
            const internal = await functions(["mongo/client", { collection: "user", find: { filter: { internal: true, notice: true } } }]) as KV[];

            await cancelBookingMail(functions, booking, doctor, external, internal);
            await deleteDataMutation({ _id: id })

            getBookings()
        } catch (e) {
            if (e instanceof Error) await showMessage(`エラー：予約を削除中にエラーが発生しました。\r\n${e.message}`, { error: true })
        }
    }, [functions, deleteDataMutation, getBookings])

    const handleActionClick = useCallback((datum: KV, action: 'edit' | 'delete' | 'print') => (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        e.stopPropagation()
        if (action === 'edit') {
            navigate(`/booking/edit/${datum._id}`)
        } else if (action === 'print') {
            makePdf(datum)
        } else if (action === 'delete') {
            handleDelete(datum._id)
        }
    }, [navigate, handleDelete])


    const columns = useMemo<ColumnDef<KV>[]>(() => {
        const actionColumn: ColumnDef<KV> = {
            id: 'actions',
            header: '',
            enableSorting: false,
            cell: ({ row }) => (
                <div className="flex gap-1">
                    {[OUTSIDE_TYPE, INTERNAL_TYPE, OUTER_BOOK_TYPE].includes(row.original.bookingType) && (
                        <button
                            className={IconClass}
                            type="button"
                            onClick={handleActionClick(row.original, 'edit')}
                            data-id={row.original._id}
                        >
                            <PencilSquareIcon />
                        </button>
                    )}
                    {[OUTSIDE_TYPE, INTERNAL_TYPE].includes(row.original.bookingType) && (
                        <button
                            className={IconClass}
                            type="button"
                            onClick={handleActionClick(row.original, 'delete')}
                            value={row.original._id}
                        >
                            <TrashIcon />
                        </button>
                    )}
                    {row.original.bookingType === OUTSIDE_TYPE && (
                        <button
                            className={IconClass}
                            type="button"
                            data-print="print"
                            onClick={handleActionClick(row.original, 'print')}
                            data-id={row.original._id}
                        >
                            <PrinterIcon />
                        </button>
                    )}
                </div>
            ),
        }

        const dataColumns: ColumnDef<KV>[] = createColumnsFromHeads(heads, { noslotResources })

        return [actionColumn, ...dataColumns]
    }, [noslotResources, handleActionClick])

    const handleDateChange = (name: string, value: any) => {
        switch (name) {
            case "startDate":
                const dateStart = new Date(value)
                filterStates({ ...filterStates(), bookingList: { ...names, start: dateStart, end: names.oneday ? new Date(dateStart.getTime() + 86399999) : names.end } })
                break
            case "endDate":
                setValue("end", new Date(value.getTime() + 86399999))
                break
            default:

        }
    }
    const handleDateMove = (e: MouseEvent<HTMLButtonElement>) => {
        let dateStart = names.start
        let dateEnd = names.end
        let oneday = names.oneday
        switch (e.currentTarget.name) {
            case "previousDay":
                dateStart = new Date(dateStart.getTime() - 86400000)
                dateEnd = new Date(dateEnd.getTime() - 86400000)
                break
            case "today":
                dateStart = today()
                dateEnd = new Date(today().getTime() + 86399999)
                break
            case "nextDay":
                dateStart = new Date(dateStart.getTime() + 86400000)
                dateEnd = new Date(dateEnd.getTime() + 86400000)
                break
            case "oneday":
                oneday = !oneday
                if (oneday) {
                    dateEnd = new Date(dateStart.getTime() + 86399999)
                }
                break
            default:

        }
        filterStates({ ...filterStates(), bookingList: { ...names, oneday: oneday, start: dateStart, end: dateEnd } })
    }

    const setValue = (name: string, value: any) => {
        filterStates({ ...filterStates(), bookingList: { ...names, [name]: value } })
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (e.currentTarget.name.startsWith("status_")) {
            let statuses: number[] = [];
            [0, 1, 2, 3, 4, 5].forEach(key => {
                if ((document.getElementById(`status_${key}`) as HTMLInputElement).checked) statuses.push(key)
            })
            setValue("statuses", statuses)
        } else {
            setValue(e.currentTarget.name, e.currentTarget.value)
        }
    }


    const handleDownload = async (e: MouseEvent<HTMLButtonElement>) => {
        const csvHeader = [...heads.map(v => v.text), "臨床診断名", "予約依頼日", "施設", "診療科", "医師"].join(',') + '\n';
        const csvData = showingData.map(datum => [
            options.bookingStatus[datum.bookingStatus],
            JSTDate(new Date(datum.start)),
            datum.checkin,
            noslotResources.includes(datum.modalityID) ? "" : JSTTime(new Date(datum.start)),
            datum.begin,
            datum.checkout,
            datum._id,
            datum.modalityID,
            options.bookingType[datum.bookingType],
            datum.courseName,
            datum.body2 || "",
            (datum.contrast === true || datum.contrast === "造影") ? "◯" : "",
            datum.patientID,
            datum.patientName,
            datum.patient?.nameKana || '',
            datum.patient?.gender ? options.genderType[datum.patient.gender] : "",
            datum.patient?.birthDate && JSTDate(new Date(datum.patient?.birthDate)),
            datum.patient?.birthDate && getAge(new Date(datum.patient?.birthDate)),
            datum.height,
            datum.weight,
            datum.diagnosis,
            datum.created && JSTDate(new Date(datum.created)),
            datum.clinicName,
            datum.department,
            datum.doctor
        ].join(',')).join('\n');
        const csvContent = csvHeader + csvData;
        const csvFilePath = 'bookingdata.csv';
        const url = window.URL.createObjectURL(new Blob([csvContent]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', csvFilePath)
        document.body.appendChild(link)
        link.click()
        link.remove()
    }

    const filterControls = (
        <>
            <button className={IconClass} type="button" onClick={() => getBookings()}><ArrowPathIcon /></button>
            <button className={IconClass} type="button" onClick={handleDownload}><ArrowDownTrayIcon /></button>
            <select className={InputClass} name="modality" value={names.modality} onChange={handleChange}><option value="">全モダリティ</option>{
                baseResources.map(resource => <option key={resource.id} value={resource.id}>{resource.title}</option>)
            }</select>
            <select className={InputClass} name="modality2" value={names.modality2} onChange={handleChange}><option value="">- 未選択</option>{
                baseResources.map(resource => <option key={resource.id} value={resource.id}>{resource.title}</option>)
            }</select>
            <button type="button" className={SmallButtonClass} name="previousDay" onClick={handleDateMove}><ChevronLeftIcon className="w-5 h-5" /></button>
            <DatePicker name="startDate" value={names.start} setValue={handleDateChange} />
            {!names.oneday && <>
                <span className="">〜</span>
                <DatePicker name="endDate" value={names.end} setValue={handleDateChange} />
            </>}
            <button type="button" className={SmallButtonClass} name="nextDay" onClick={handleDateMove}><ChevronRightIcon className="w-5 h-5" /></button>
            <button type="button" className={SmallButtonClass} name="oneday" onClick={handleDateMove}>{names.oneday ? "複数日" : "単日"}</button>
            <button type="button" className={SmallButtonClass} name="today" onClick={handleDateMove}>本日</button>
            <FilterInput label="患者ID" name="patientID" value={names.patientID} setValue={setValue} />
            <FilterInput label="患者名カナ" name="patientName" value={names.patientName} setValue={setValue} />
            <div className="flex items-center gap-2">
                {statusSet.map(status => (
                    <React.Fragment key={status.key}>
                        <input
                            className={CheckClass}
                            type="checkbox"
                            name={`status_${status.key}`}
                            id={`status_${status.key}`}
                            checked={Boolean(names.statuses.includes(status.key))}
                            onChange={handleChange}
                        />
                        <label className="pr-4 whitespace-nowrap">{status.value}</label>
                    </React.Fragment>
                ))}
            </div>
        </>
    )



    useEffect(() => {
        getBookings()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values])

    if (loading || deleting) {
        return <Loading full />
    }

    return (
        <DataTable
            data={showingData}
            columns={columns}
            filterContent={filterControls}
            onAdd={() => navigate('/booking/create')}
            itemCount={showingData.length}
            getRowProps={(row) => ({
                key: (row.original as KV)._id,
                'data-id': (row.original as KV)._id,
                'data-detail': 'detail',
                className: 'text-sm text-gray-900 cursor-pointer hover:bg-theme-50',
                onClick: () => navigate(`/booking/detail/${(row.original as KV)._id}`)
            })}
            getCellProps={(cell) => {
                if (cell.column.id === 'bookingStatus') {
                    const booking = cell.row.original as KV
                    return { className: `text-white bg-${getStatusColor(booking.bookingStatus)}` }
                }
                return {}
            }}
            storageKey="bookingList_columnSettings"
        />
    )

}

export default List
